/**
 * 샵관리 — 시술기록 (핵심 화면)
 * 고객·서비스 선택 → 회원권 잔여횟수 표시 → BOM 사용제품 자동 채움 → 저장 시
 * 재고 FIFO 출고 + 회차 차감 + 원가 확정이 한 번에 처리된다. 삭제하면 전부 되돌린다.
 */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { hiCol } from 'components/grid/hiGrid';
import { ActionButton } from 'styles/common.styles';
import { showAlert } from 'utils/toastBridge';
import ShopGridPage from './ShopGridPage';
import {
  loadTable, customerOptions, serviceOptions, productOptions, activePassesOf, passRemain,
  serviceItemsOf, saveRecord, deleteRecord, stockSummary, nowLocal, num, fmt, createUid,
} from 'shop/shopStore';
import { kwField, dateRangeFields, inRange } from 'shop/shopPageHelpers';

const Panel = styled.div`
  border: 1px solid #d9dde3;
  border-radius: 4px;
  padding: 8px 10px;
  margin: 4px 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
  }
  select,
  input {
    height: 24px;
    font-size: 12px;
    border: 1px solid #c5cad3;
    border-radius: 3px;
    padding: 0 4px;
  }
  .hint {
    font-size: 12px;
    color: #5b6472;
  }
  .warn {
    font-size: 12px;
    color: #c0392b;
  }
`;

const RecordForm = ({ tc, research, reload }) => {
  const [f, setF] = useState({ customerUid: '', serviceUid: '', passUid: '', visitDt: nowLocal(), staffNm: '', remark: '' });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const customers = useMemo(() => customerOptions(tc), [tc]);
  const services = useMemo(() => serviceOptions(tc), [tc]);
  const products = useMemo(() => productOptions(tc), [tc]);
  const stock = useMemo(() => new Map(stockSummary(tc).map((s) => [s.productUid, s])), [tc, busy]); // eslint-disable-line react-hooks/exhaustive-deps
  const passes = useMemo(
    () => (f.customerUid ? activePassesOf(tc, f.customerUid, f.serviceUid) : []),
    [tc, f.customerUid, f.serviceUid, busy] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const onService = (serviceUid) => {
    const bom = serviceItemsOf(tc, serviceUid).map((i) => ({ key: createUid('K'), productUid: i.productUid, useQty: i.useQty }));
    setItems(bom);
    const list = f.customerUid ? activePassesOf(tc, f.customerUid, serviceUid) : [];
    set({ serviceUid, passUid: list.length === 1 ? list[0].passUid : '' });
  };
  const onCustomer = (customerUid) => {
    const list = customerUid ? activePassesOf(tc, customerUid, f.serviceUid) : [];
    set({ customerUid, passUid: list.length === 1 ? list[0].passUid : '' });
  };

  const previewCost = items.reduce((s, i) => s + num(i.useQty) * (stock.get(i.productUid)?.headCost || 0), 0);
  const shortage = items.filter((i) => i.productUid && num(i.useQty) > (stock.get(i.productUid)?.remainQty || 0));

  const submit = () => {
    setBusy(true);
    try {
      const rec = saveRecord(tc, { ...f, items });
      const pass = f.passUid ? loadTable(tc, 'pass').find((p) => p.passUid === f.passUid) : null;
      showAlert(
        `저장했습니다. 원가 ₩${fmt(rec.costAmount)}${pass ? ` · 회원권 잔여 ${passRemain(pass)}회` : ' · 회원권 없이(단건) 기록'}`,
        'success'
      );
      setItems(serviceItemsOf(tc, f.serviceUid).map((i) => ({ key: createUid('K'), productUid: i.productUid, useQty: i.useQty })));
      set({ visitDt: nowLocal(), remark: '' });
      reload();
      research();
    } catch (e) {
      showAlert(e.message || '저장 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <Row>
        <label>고객
          <select value={f.customerUid} onChange={(e) => onCustomer(e.target.value)}>
            <option value="">선택</option>
            {customers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label>서비스
          <select value={f.serviceUid} onChange={(e) => onService(e.target.value)}>
            <option value="">선택</option>
            {services.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label>회원권
          <select value={f.passUid} onChange={(e) => set({ passUid: e.target.value })}>
            <option value="">사용 안 함(단건)</option>
            {passes.map((p) => (
              <option key={p.passUid} value={p.passUid}>
                {(services.find((s) => s.value === p.serviceUid)?.label || '').replace(/^\S+\s/, '')} 잔여 {passRemain(p)}/{p.totalCnt}회
              </option>
            ))}
          </select>
        </label>
        <label>일시
          <input value={f.visitDt} onChange={(e) => set({ visitDt: e.target.value })} placeholder="yyyy-MM-dd HH:mm" style={{ width: 140 }} />
        </label>
        <label>담당 원장
          <input value={f.staffNm} onChange={(e) => set({ staffNm: e.target.value })} style={{ width: 90 }} />
        </label>
        <label>메모
          <input value={f.remark} onChange={(e) => set({ remark: e.target.value })} style={{ width: 200 }} />
        </label>
      </Row>
      {f.customerUid && f.serviceUid && passes.length === 0 && (
        <Row><span className="hint">이 고객의 이용중 회원권이 없어 단건으로 기록됩니다. (매출은 매출등록에서 별도 입력)</span></Row>
      )}
      <Row>
        <span className="hint">사용 제품:</span>
        {items.map((it) => (
          <span key={it.key} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <select value={it.productUid} onChange={(e) => setItems(items.map((x) => (x.key === it.key ? { ...x, productUid: e.target.value } : x)))}>
              <option value="">제품</option>
              {products.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              type="number"
              min="0"
              step="any"
              style={{ width: 64 }}
              value={it.useQty}
              onChange={(e) => setItems(items.map((x) => (x.key === it.key ? { ...x, useQty: e.target.value } : x)))}
            />
            <span className="hint">{stock.get(it.productUid)?.useUnit || ''} (재고 {fmt(stock.get(it.productUid)?.remainQty || 0)})</span>
            <ActionButton type="button" onClick={() => setItems(items.filter((x) => x.key !== it.key))}>✕</ActionButton>
          </span>
        ))}
        <ActionButton type="button" onClick={() => setItems([...items, { key: createUid('K'), productUid: '', useQty: 1 }])}>＋ 제품</ActionButton>
      </Row>
      <Row>
        <span className="hint">예상 원가(FIFO 현재단가): ₩{fmt(Math.round(previewCost))}</span>
        {shortage.length > 0 && <span className="warn">재고 부족: {shortage.map((i) => stock.get(i.productUid)?.productNm).join(', ')} — 입고 후 저장하세요</span>}
        <ActionButton type="button" disabled={busy || !f.customerUid || !f.serviceUid} onClick={submit}>💾 시술 저장</ActionButton>
      </Row>
    </Panel>
  );
};

const decorate = (tc) => {
  const customers = new Map(loadTable(tc, 'customer').map((c) => [c.customerUid, c]));
  const services = new Map(loadTable(tc, 'service').map((s) => [s.serviceUid, s]));
  const items = loadTable(tc, 'recordItem');
  const products = new Map(loadTable(tc, 'product').map((p) => [p.productUid, p]));
  return (r) => ({
    ...r,
    mod: '',
    customerNm: customers.get(r.customerUid)?.customerNm || '',
    serviceNm: services.get(r.serviceUid)?.serviceNm || '',
    usedProducts: items
      .filter((i) => i.recordUid === r.recordUid)
      .map((i) => `${products.get(i.productUid)?.productNm || '-'} ${num(i.useQty)}${products.get(i.productUid)?.useUnit || ''}`)
      .join(', '),
  });
};

const filter = (rows, v) =>
  rows
    .filter(
      (r) => r.mod !== 'D'
        && inRange(r.visitDt, v.fromDate, v.toDate)
        && (!v.keyword || `${r.customerNm} ${r.serviceNm} ${r.staffNm}`.includes(v.keyword))
    )
    .sort((a, b) => String(b.visitDt).localeCompare(String(a.visitDt)));

const cfg = {
  gridId: 'sh-record',
  title: '시술기록',
  uidKey: 'recordUid',
  noAdd: true,
  searchDefaults: { keyword: '', fromDate: '', toDate: '' },
  searchFields: () => [[...dateRangeFields(), kwField('고객·서비스·원장')]],
  renderTop: (ctx) => <RecordForm {...ctx} />,
  columns: () => [
    hiCol.roTextColumn({ header: '일시', field: 'visitDt', size: 140, align: 'center' }),
    hiCol.roTextColumn({ header: '고객', field: 'customerNm', size: 100 }),
    hiCol.roTextColumn({ header: '서비스', field: 'serviceNm', size: 170 }),
    hiCol.roNumberColumn({ header: '회차', field: 'sessionNo', size: 70 }),
    hiCol.roTextColumn({ header: '담당 원장', field: 'staffNm', size: 90 }),
    hiCol.roTextColumn({ header: '사용 제품', field: 'usedProducts', size: 260 }),
    hiCol.roNumberColumn({ header: '원가', field: 'costAmount', size: 100 }),
    hiCol.roTextColumn({ header: '메모', field: 'remark', size: 200 }),
  ],
  load: (tc) => loadTable(tc, 'record').map(decorate(tc)),
  filter,
  validate: () => [],
  // 이 화면의 저장 = 삭제 반영(출고·회차 역처리). 신규 입력은 위 입력영역에서만 한다.
  save: (tc, rows) => {
    rows.filter((r) => r.mod === 'D').forEach((r) => deleteRecord(tc, r.recordUid));
  },
};

const ServiceRecord = () => <ShopGridPage cfg={cfg} />;
export default ServiceRecord;
